import * as React from "react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TemplateBlock } from "../../types/blockTypes";
import { Box, Eye, MousePointer, Check } from "lucide-react";
import { ICON_SIZES } from "@/lib/icon-sizes";
import { cn } from "@/lib/utils";

interface BlockCardProps {
  block: TemplateBlock;
  placeholderCount: number;
  currentPlaceholderIndex: number;
  onInsertAtCursor: (block: TemplateBlock) => Promise<void>;
  onInsertAtPlaceholder: (block: TemplateBlock, placeholderIndex: number) => Promise<void>;
  onPreview?: (block: TemplateBlock) => void;
}

const BlockCard: React.FC<BlockCardProps> = ({
  block,
  placeholderCount,
  currentPlaceholderIndex,
  onInsertAtCursor,
  onInsertAtPlaceholder,
  onPreview
}) => {
  const [isInserting, setIsInserting] = useState(false);
  const [showPlaceholderSelect, setShowPlaceholderSelect] = useState(false);
  const [selectedPlaceholder, setSelectedPlaceholder] = useState<string>("");
  const [placeholderConfirmationMode, setPlaceholderConfirmationMode] = useState(false);

  // Resetear estados cuando cambia el bloque
  useEffect(() => {
    setShowPlaceholderSelect(false);
    setPlaceholderConfirmationMode(false);
    setSelectedPlaceholder("");
  }, [block.id]);

  // Preseleccionar el placeholder actual cuando se abre el select
  useEffect(() => {
    if (showPlaceholderSelect && currentPlaceholderIndex >= 0 && currentPlaceholderIndex < placeholderCount) {
      setSelectedPlaceholder((currentPlaceholderIndex + 1).toString());
      setPlaceholderConfirmationMode(true);
    }
  }, [showPlaceholderSelect, currentPlaceholderIndex, placeholderCount]);

  const handleInsertCursor = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsInserting(true);
    try {
      await onInsertAtCursor(block);
    } finally {
      setIsInserting(false);
    }
  };

  const handlePlaceholderChange = (value: string) => {
    setSelectedPlaceholder(value);
    setPlaceholderConfirmationMode(true);
  };

  const handleConfirmPlaceholder = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const index = parseInt(selectedPlaceholder);
    setIsInserting(true);
    try {
      await onInsertAtPlaceholder(block, index);
    } finally {
      setIsInserting(false);
      setShowPlaceholderSelect(false);
      setPlaceholderConfirmationMode(false);
      setSelectedPlaceholder("");
    }
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPreview?.(block);
  };

  const togglePlaceholderSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showPlaceholderSelect) {
      // Cerrar y resetear
      setShowPlaceholderSelect(false);
      setPlaceholderConfirmationMode(false);
      setSelectedPlaceholder("");
    } else {
      // Abrir
      setShowPlaceholderSelect(true);
    }
  };

  return (
    <div
      className={cn(
        "bg-white border border-gray-100 rounded-xl p-4 mb-3 transition-all cursor-default hover:border-gray-200 hover:shadow-md shadow-sm",
        isInserting && "opacity-50 pointer-events-none"
      )}
    >
      <div className="flex items-center gap-4">
        {/* Icono */}
        <div className="w-10 h-10 rounded-lg border border-gray-100 flex items-center justify-center flex-shrink-0 bg-gray-50 text-gray-500">
          <Box size={ICON_SIZES.md} strokeWidth={2} />
        </div>

        {/* Contenido */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 mb-1 overflow-hidden text-ellipsis whitespace-nowrap">
            {block.title}
          </h3>
          {block.description && (
            <p className="text-xs text-gray-500 m-0 overflow-hidden text-ellipsis line-clamp-2 leading-relaxed">
              {block.description}
            </p>
          )}
        </div>

        {/* Botones de acción */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Botón Vista Previa */}
          {onPreview && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePreview}
              className="h-8 w-8 p-0 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
              title="Preview"
            >
              <Eye size={ICON_SIZES.sm} />
            </Button>
          )}

          {/* Botón: Insertar en cursor */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleInsertCursor}
            disabled={isInserting}
            className="h-8 px-3 text-xs hover:bg-gray-100"
            title="Insert at cursor"
          >
            <MousePointer size={ICON_SIZES.xs} className="mr-1" />
            Cursor
          </Button>
          
          {/* Selector de placeholder */}
          {placeholderCount > 0 && (
            <>
              {!showPlaceholderSelect ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={togglePlaceholderSelect}
                  disabled={isInserting}
                  className="h-8 w-8 p-0 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  title="Insert at placeholder"
                >
                  <span className="text-xs font-mono font-semibold">{"{}"}</span>
                </Button>
              ) : (
                <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5">
                  <Select 
                    value={selectedPlaceholder}
                    onValueChange={handlePlaceholderChange}
                  >
                    <SelectTrigger className="h-8 w-32 text-xs">
                      <SelectValue placeholder="Choose" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: placeholderCount }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()} className="text-xs">
                          <span className="font-mono text-xs">{`{{bloque}}`}</span> #{i + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {placeholderConfirmationMode && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleConfirmPlaceholder}
                      disabled={isInserting}
                      className="h-8 px-3 text-xs bg-muted hover:bg-muted/90"
                      title="Confirm insertion"
                    >
                      <Check size={ICON_SIZES.xs} className="mr-1" />
                      Confirm
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlockCard;

